/**
 * Per-user preferences.
 *
 * Stored in `localStorage` keyed by user id so different mock users on
 * the same machine get separate settings. Defaults match the
 * Phase-1 baseline so a fresh user gets a sane experience without
 * touching the profile.
 *
 * The store exposes plain getters/setters; the React binding lives in
 * `useUserPreferences.ts` so screens can subscribe + re-render when
 * a value changes.
 */
import type { TableLayout } from './table-layout';
import {
  CLEANED_DAYS_DEFAULT,
  clampCleanedDays,
} from '../screens/Twin/cleaned-zones';

/**
 * Low-battery pop-ups on this computer (Akash, 2026-10-31, spa 0.5.69).
 *
 * The site raises ONE low-battery alert per robot, at levels that are the
 * same for everyone (`robot_battery_low`: under 20% with no job and not moved
 * or charged for 10 minutes, urgent under 10%), and everyone at the hotel
 * sees it in the bell (and on the Alerts screen, in a role that has one).
 * What a person chooses here is
 * only whether the LOW one pops up -- toast, chime and desktop notification --
 * on this computer. VERY LOW always pops up: there is no setting that hides a
 * robot about to run flat.
 *
 * It replaced four number boxes (warning and critical thresholds and steps)
 * that filtered a `robot_battery` field no alert row ever carried, and two of
 * which nothing read at all.
 */
export interface BatteryAlertPreferences {
  /** Pop up for a low battery (under 20%). Default on. */
  lowPopups: boolean;
}

/** Fire-alarm audio override. The mute toggle in the TopBar applies
 *  to informational toasts; fire alarms are safety-critical and the
 *  user can opt into one of three behaviours:
 *  - `always`: play the siren regardless of the master mute.
 *  - `mute-with-rest`: play only when the master mute is OFF (default).
 *  - `never`: never play the siren even if the alarm is active. The
 *    visual overlay still shows. Reserved for sites where the alarm
 *    is wired to a separate physical klaxon.
 */
export type FireAlarmAudioMode = 'always' | 'mute-with-rest' | 'never';

/** Settings panel display preferences. */
export interface UiPreferences {
  /** Maximum dropdown height (px) before the bell list scrolls. */
  bellMaxHeightPx: number;
  /** Auto-archive read alerts older than this many minutes. */
  autoArchiveAfterMinutes: number;
}

export type ThemePreference = 'system' | 'light' | 'dark';

export interface UserPreferences {
  battery: BatteryAlertPreferences;
  fireAlarmAudio: FireAlarmAudioMode;
  ui: UiPreferences;
  theme: ThemePreference;
  /**
   * In-product Help mode: the explanations the console shows about
   * itself. Deliberately per-user-per-BROWSER (this whole store is
   * localStorage keyed by user id), which is what was asked for: it is
   * enough to stop the nudge pestering someone who has already read
   * the help, and it does not follow them from the front-desk PC to a
   * tablet. There is no platform-side training record and none was
   * wanted.
   */
  help: HelpPreferences;
  /**
   * Each person's own column layout per table, keyed by table name
   * (`tasks`). Per browser like the rest of this store, which is what
   * was asked for: a layout set up on the front-desk PC does not follow
   * anyone to an iPad, where fewer columns fit anyway.
   *
   * A table with no entry shows its default layout. Resetting REMOVES
   * the entry rather than saving a copy of the default -- see
   * `isDefaultLayout` in `table-layout.ts` for why.
   */
  tableLayouts: Record<string, TableLayout>;
  /**
   * Cleaned-zone colouring (Akash, 2026-10-22): which views colour the
   * cleaning zones green-to-grey by when they were last cleaned, and how
   * many days it takes a zone to turn fully grey. Set here and nowhere
   * else -- the views carry no control of their own, only a legend.
   */
  cleanedZones: CleanedZonePreferences;
}

export interface CleanedZonePreferences {
  /** Colour the zones on the Overview's Building view (3D). Default on. */
  building: boolean;
  /** Colour the zones on the Overview's 2D map. Default off. */
  map: boolean;
  /** Days until a cleaned zone is fully grey: an integer, 1-14. Default 3. */
  days: number;
}

export interface HelpPreferences {
  /** Is Help mode showing right now. */
  mode: boolean;
  /**
   * Has this person ever turned it on. The ONLY thing this drives is
   * whether the toggle still wears its "there is help here" dot -- it
   * is a nudge that stops nagging, not a completion record.
   */
  seen: boolean;
}

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  battery: {
    lowPopups: true,
  },
  fireAlarmAudio: 'mute-with-rest',
  ui: {
    bellMaxHeightPx: 320,
    autoArchiveAfterMinutes: 60,
  },
  theme: 'system',
  help: {
    // OFF by default. Help mode adds a paragraph to every screen, and a
    // console that explains itself unprompted to someone who already
    // knows it is noise in a room where people are working.
    mode: false,
    seen: false,
  },
  tableLayouts: {},
  cleanedZones: {
    building: true,
    map: false,
    days: CLEANED_DAYS_DEFAULT,
  },
};

const STORAGE_PREFIX = 'nx:user-prefs';

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

/**
 * The low-battery choice ALSO lives under a key of its own, which wins over
 * the copy in the main object (Akash, 2026-10-31: "an old tab rebuilding
 * prefs must not break the new field").
 *
 * WHY. Every tab rebuilds the whole object field by field on load and writes
 * all of it back on any change (`mergePreferences`, `useUserPreferences`).
 * A tab still running spa 0.5.68 or older knows `battery` as the four old
 * numbers, so the first time it saves anything -- the Help toggle, a column
 * layout -- it writes `battery` back without `lowPopups`, and an untick made
 * in a new tab would silently come back on. No old tab knows this key, so
 * none can touch it. It is written only when the choice itself changes (or
 * on a reset), never on another field's save, so a second new tab holding
 * the choice from before cannot put it back either.
 */
function batteryStorageKey(userId: string): string {
  return `${STORAGE_PREFIX}-battery:${userId}`;
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function stringList(v: unknown): string[] | null {
  return Array.isArray(v) && v.every((x) => typeof x === 'string') ? (v as string[]) : null;
}

/**
 * Stored layouts, kept only where they are the right SHAPE. Which ids
 * they name is not checked here: this store does not know any table's
 * columns, and `resolveTableLayout` already drops an id the table no
 * longer has. A malformed entry is dropped whole -- half a layout would
 * reorder somebody's columns into something they never set.
 */
function mergeTableLayouts(v: unknown): Record<string, TableLayout> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: Record<string, TableLayout> = {};
  for (const [table, raw] of Object.entries(v as Record<string, unknown>)) {
    if (!raw || typeof raw !== 'object') continue;
    const order = stringList((raw as Record<string, unknown>).order);
    const hidden = stringList((raw as Record<string, unknown>).hidden);
    if (order && hidden) out[table] = { order, hidden };
  }
  return out;
}

function mergePreferences(loaded: unknown): UserPreferences {
  // Shallow-merge loaded overrides on top of the defaults so adding
  // a new key in a future release doesn't blow away user settings.
  const def = DEFAULT_USER_PREFERENCES;
  if (!loaded || typeof loaded !== 'object') return def;
  const o = loaded as Record<string, unknown>;
  const bat = (o.battery ?? {}) as Record<string, unknown>;
  const fire = (o.fireAlarmAudio ?? def.fireAlarmAudio) as FireAlarmAudioMode;
  const ui = (o.ui ?? {}) as Record<string, unknown>;
  const theme = (o.theme ?? def.theme) as ThemePreference;
  const help = (o.help ?? {}) as Record<string, unknown>;
  const cleaned = (o.cleanedZones ?? {}) as Record<string, unknown>;
  return {
    // The four old threshold numbers an older save carries are dropped here.
    // The choice's own key, read in `loadUserPreferences`, wins over this.
    battery: {
      lowPopups: typeof bat.lowPopups === 'boolean' ? bat.lowPopups : def.battery.lowPopups,
    },
    fireAlarmAudio: ['always', 'mute-with-rest', 'never'].includes(fire)
      ? fire
      : def.fireAlarmAudio,
    ui: {
      bellMaxHeightPx: isFiniteNumber(ui.bellMaxHeightPx)
        ? ui.bellMaxHeightPx
        : def.ui.bellMaxHeightPx,
      autoArchiveAfterMinutes: isFiniteNumber(ui.autoArchiveAfterMinutes)
        ? ui.autoArchiveAfterMinutes
        : def.ui.autoArchiveAfterMinutes,
    },
    theme: ['system', 'light', 'dark'].includes(theme) ? theme : def.theme,
    // THIS FUNCTION REBUILDS THE OBJECT FIELD BY FIELD rather than
    // spreading, so a key added to the interface and to the defaults
    // and NOT added here is silently dropped on every load -- the
    // setting appears to save and is gone on refresh, with nothing
    // logged. Anyone adding a preference must add a line here too.
    help: {
      mode: typeof help.mode === 'boolean' ? help.mode : def.help.mode,
      seen: typeof help.seen === 'boolean' ? help.seen : def.help.seen,
    },
    tableLayouts: mergeTableLayouts(o.tableLayouts),
    cleanedZones: {
      building:
        typeof cleaned.building === 'boolean' ? cleaned.building : def.cleanedZones.building,
      map: typeof cleaned.map === 'boolean' ? cleaned.map : def.cleanedZones.map,
      // A hand-edited or older value is clamped, never trusted: the views
      // divide by it.
      days: cleaned.days === undefined ? def.cleanedZones.days : clampCleanedDays(cleaned.days),
    },
  };
}

/** The low-battery choice from its own key, or null when none was saved
 *  there (or it is unreadable). See `batteryStorageKey`. */
function loadBatteryPreferences(userId: string): BatteryAlertPreferences | null {
  try {
    const raw = window.localStorage.getItem(batteryStorageKey(userId));
    if (!raw) return null;
    const v = JSON.parse(raw) as Record<string, unknown> | null;
    return v && typeof v.lowPopups === 'boolean' ? { lowPopups: v.lowPopups } : null;
  } catch {
    return null;
  }
}

export function loadUserPreferences(userId: string): UserPreferences {
  if (typeof window === 'undefined' || !userId) return DEFAULT_USER_PREFERENCES;
  let prefs = DEFAULT_USER_PREFERENCES;
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (raw) prefs = mergePreferences(JSON.parse(raw));
  } catch {
    prefs = DEFAULT_USER_PREFERENCES;
  }
  const battery = loadBatteryPreferences(userId);
  return battery ? { ...prefs, battery } : prefs;
}

export function saveUserPreferences(userId: string, prefs: UserPreferences): void {
  if (typeof window === 'undefined' || !userId) return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable (private mode, quota); ignore.
  }
}

/** Save the low-battery choice under its own key. Called only when the
 *  choice itself changes, or on a reset (`useUserPreferences`). */
export function saveBatteryPreferences(userId: string, battery: BatteryAlertPreferences): void {
  if (typeof window === 'undefined' || !userId) return;
  try {
    window.localStorage.setItem(
      batteryStorageKey(userId),
      JSON.stringify({ lowPopups: battery.lowPopups }),
    );
  } catch {
    // localStorage may be unavailable (private mode, quota); ignore.
  }
}
